import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  CCC_CLIENT_TYPE_LABELS,
  type CccClientType,
} from '../../config/cccClientRoutes';
import {
  CccCalEvent,
  formatDateLong,
  formatTime,
  parseIsoLocal,
} from './cccCalTypes';

type Props = {
  event: CccCalEvent;
  anchor: DOMRect;
  onClose: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onOpenClient?: () => void;
  busy?: boolean;
};

const POPOVER_WIDTH = 360;
const GAP = 8;

function snapshotLines(snap: Record<string, unknown> | null | undefined): Array<{ label: string; value: string }> {
  if (!snap || typeof snap !== 'object') return [];
  const rows: Array<{ label: string; value: string }> = [];
  const push = (label: string, raw: unknown) => {
    if (raw == null || raw === '') return;
    rows.push({ label, value: String(raw) });
  };
  push('Responsável', snap.nome_responsavel);
  push('Contacto', snap.contacto);
  push('Email', snap.email);
  push('NIF', snap.nif);
  push('Produto', snap.produto);
  push('Empresa', snap.nome_empresa);
  push('Marcação', snap.marcacao_id);
  push('Lead', snap.lead_id);
  return rows;
}

const CccEventPopover: React.FC<Props> = ({
  event,
  anchor,
  onClose,
  onEdit,
  onDelete,
  onOpenClient,
  busy = false,
}) => {
  const [position, setPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [ready, setReady] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const height = rect.height || 280;
    const width = rect.width || POPOVER_WIDTH;

    let left = anchor.right + GAP;
    if (left + width > window.innerWidth - 8) {
      left = anchor.left - width - GAP;
    }
    if (left < 8) left = Math.max(8, window.innerWidth / 2 - width / 2);

    let top = anchor.top;
    if (top + height > window.innerHeight - 8) {
      top = Math.max(8, window.innerHeight - height - 8);
    }
    if (top < 8) top = 8;

    setPosition({ top, left });
    setReady(true);
  }, [anchor, event.id]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const start = parseIsoLocal(event.starts_at);
  const end = parseIsoLocal(event.ends_at);
  const tone = event.tone as CccClientType;
  const typeLabel = CCC_CLIENT_TYPE_LABELS[tone] || 'Evento';
  const src = event.source;
  const hasClient = src.client_type !== 'none' && Boolean(src.client_ref || src.client_label);
  const snapRows = snapshotLines(src.client_snapshot as Record<string, unknown> | null);

  return (
    <>
      <div className="grh-popover-scrim" onClick={onClose} role="presentation" />
      <div
        ref={ref}
        className={`grh-popover ccc-event-detail area-${event.tone}${ready ? ' is-ready' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Detalhe do evento"
        style={{ top: position.top, left: position.left, width: POPOVER_WIDTH }}
      >
        <button type="button" className="grh-popover-close" onClick={onClose} aria-label="Fechar">
          ×
        </button>

        <div className={`grh-popover-area area-${event.tone}`}>{typeLabel}</div>
        <h3 className="grh-popover-title">{src.title || event.title}</h3>

        <dl className="ccc-event-detail-fields">
          <div className="ccc-event-detail-row">
            <dt>Início</dt>
            <dd>
              {formatDateLong(start)} · {formatTime(start)}
            </dd>
          </div>
          <div className="ccc-event-detail-row">
            <dt>Fim</dt>
            <dd>
              {formatDateLong(end)} · {formatTime(end)}
            </dd>
          </div>
          <div className="ccc-event-detail-row">
            <dt>Área do cliente</dt>
            <dd>{typeLabel}</dd>
          </div>
          {event.ownerName && (
            <div className="ccc-event-detail-row">
              <dt>Utilizador</dt>
              <dd>{event.ownerName}</dd>
            </div>
          )}
          {hasClient && (
            <div className="ccc-event-detail-row">
              <dt>Cliente associado</dt>
              <dd>
                <strong>{src.client_label || src.client_ref}</strong>
                {src.client_ref && src.client_label && (
                  <span className="ccc-event-detail-ref"> · {src.client_ref}</span>
                )}
              </dd>
            </div>
          )}
          {snapRows.map((row) => (
            <div key={row.label} className="ccc-event-detail-row">
              <dt>{row.label}</dt>
              <dd>{row.value}</dd>
            </div>
          ))}
          {src.notes ? (
            <div className="ccc-event-detail-row ccc-event-detail-row--notes">
              <dt>Notas</dt>
              <dd>{src.notes}</dd>
            </div>
          ) : null}
        </dl>

        <div className="grh-popover-actions">
          {hasClient && onOpenClient && (
            <button type="button" className="grh-popover-btn is-primary" onClick={onOpenClient}>
              Ir para cliente
            </button>
          )}
          <button type="button" className="grh-popover-btn" onClick={onEdit} disabled={busy}>
            Editar
          </button>
          <button
            type="button"
            className="grh-popover-btn is-danger"
            onClick={onDelete}
            disabled={busy}
          >
            Eliminar
          </button>
        </div>
      </div>
    </>
  );
};

export default CccEventPopover;
